import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { History, Download, Filter, User } from "lucide-react";
import { formatDateDDMonYYYY } from "../utils/dateFormat";
import DateFilter from "./DateFilter";

// Ensure UTC timestamps are parsed correctly (MongoDB returns without Z suffix)
const parseUTC = (ts) => {
  if (!ts) return new Date();
  const str = typeof ts === 'string' && !ts.endsWith('Z') && !ts.includes('+') ? ts + 'Z' : ts;
  return new Date(str);
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLogs();
    fetchUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [logs, filterUser, startDate, endDate]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/audit-logs");
      setLogs(response.data);
    } catch (error) {
      toast.error("Failed to fetch audit logs");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/users");
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const applyFilters = () => {
    let filtered = [...logs];

    // Filter by user
    if (filterUser !== "all") {
      filtered = filtered.filter(log => log.user_id === filterUser);
    }

    // Filter by date range
    if (startDate) {
      filtered = filtered.filter(log => {
        const logDate = parseUTC(log.timestamp);
        const start = new Date(startDate);
        return logDate >= start;
      });
    }

    if (endDate) {
      filtered = filtered.filter(log => {
        const logDate = parseUTC(log.timestamp);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return logDate <= end;
      });
    }

    setFilteredLogs(filtered);
  };

  const clearFilters = () => {
    setFilterUser("all");
    setStartDate("");
    setEndDate("");
  };

  const exportToCSV = () => {
    if (filteredLogs.length === 0) {
      toast.error("No logs to export");
      return;
    }

    let csvContent = "Date,Time,User,Role,Action,Entity Type,Entity Name,Details\n";
    
    filteredLogs.forEach(log => {
      const timestamp = parseUTC(log.timestamp);
      const date = formatDateDDMonYYYY(timestamp);
      const time = timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      
      csvContent += `"${date}","${time}","${log.username}","${log.user_role}","${log.action}","${log.entity_type}","${log.entity_name}","${log.details}"\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success("Exported to CSV successfully!");
  };

  // Group logs by date
  const groupedLogs = filteredLogs.reduce((acc, log) => {
    const date = parseUTC(log.timestamp).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(log);
    return acc;
  }, {});

  const getActionColor = (action) => {
    switch (action) {
      case 'create':
        return 'text-green-600 bg-green-50';
      case 'update':
        return 'text-blue-600 bg-blue-50';
      case 'delete':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getActionText = (action) => {
    switch (action) {
      case 'create': return 'Added';
      case 'update': return 'Updated';
      case 'delete': return 'Deleted';
      default: return action;
    }
  };

  const getEntityText = (entityType) => {
    const mapping = {
      'revenue': 'revenue',
      'restaurant': 'restaurant',
      'user': 'user',
      'expense': 'expense entry',
      'balance': 'balance',
      'category': 'category',
      'brand': 'brand',
      'target': 'target'
    };
    return mapping[entityType] || entityType;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
            <History className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Audit Logs</h2>
            <p className="text-sm text-gray-600">Track all system activities and changes</p>
          </div>
        </div>
        <Button onClick={exportToCSV} className="gap-2">
          <Download className="w-4 h-4" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-xl">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            <CardTitle>Filters</CardTitle>
          </div>
          <CardDescription>Filter audit logs by user and date range</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                User
              </Label>
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger>
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.username} ({user.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DateFilter
              onFilterChange={(filterData) => {
                if (filterData.filterType === 'exact') {
                  setStartDate(filterData.exactDate);
                  setEndDate(filterData.exactDate);
                } else {
                  setStartDate(filterData.startDate);
                  setEndDate(filterData.endDate);
                }
              }}
            />

            <div className="flex items-end">
              <Button variant="outline" onClick={clearFilters} className="w-full">
                Clear Filters
              </Button>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
            <span>Total Logs: <strong>{filteredLogs.length}</strong></span>
            {(filterUser !== "all" || startDate || endDate) && (
              <span className="text-blue-600">• Filters Active</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs */}
      {loading ? (
        <Card className="p-12 text-center">
          <p className="text-gray-500">Loading audit logs...</p>
        </Card>
      ) : Object.keys(groupedLogs).length === 0 ? (
        <Card className="p-12 text-center">
          <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Audit Logs Found</h3>
          <p className="text-gray-600">
            {logs.length === 0
              ? "No activities have been logged yet"
              : "No logs match the selected filters"}
          </p>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedLogs).map(([date, dateLogs]) => (
            <Card key={date} className="shadow-lg border-0 bg-white/90 backdrop-blur-xl">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
                <CardTitle className="text-lg">{date}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {dateLogs.map(log => (
                    <div key={log.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-20 text-sm text-gray-500">
                          {parseUTC(log.timestamp).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900">{log.username}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getActionColor(log.action)}`}>
                              {getActionText(log.action)}
                            </span>
                            <span className="text-gray-700">
                              {log.action === 'create' && 'a'}
                              {log.action === 'update' && 'the'}
                              {log.action === 'delete' && 'the'}
                              {' '}{getEntityText(log.entity_type)}
                            </span>
                            {log.entity_name && (
                              <span className="font-medium text-gray-900">{log.entity_name}</span>
                            )}
                          </div>
                          {log.details && (
                            <p className="text-sm text-gray-600 mt-1">{log.details}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            <span className="px-2 py-0.5 bg-gray-100 rounded">{log.user_role}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
